import { useCallback, useEffect, useState } from "react";
import { PageHeader } from "@/components/common/PageHeader";
import { FilterSelect } from "@/components/common/FilterSelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { apiErrorMessage, outreachApi } from "@/lib/api";
import { toast } from "sonner";
import { Copy, Plus, Star, Trash2 } from "lucide-react";

export function OutreachTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [folders, setFolders] = useState([]);
  const [q, setQ] = useState("");
  const [folder, setFolder] = useState("");
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    name: "",
    channel: "email",
    category: "General",
    folder: "Inbox",
    subject: "",
    body_text: "",
    tags: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await outreachApi.listTemplates({
        q: q || undefined,
        folder: folder || undefined,
      });
      setTemplates(r.templates || []);
      setFolders(r.folders || []);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load templates"));
    } finally {
      setLoading(false);
    }
  }, [q, folder]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setCreating(true);
    try {
      await outreachApi.createTemplate({
        name: form.name.trim(),
        channel: form.channel,
        category: form.category,
        folder: form.folder || "Inbox",
        subject: form.subject || null,
        body_text: form.body_text,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
        variables: ["first_name", "name"],
      });
      toast.success("Template created");
      setForm({ name: "", channel: "email", category: "General", folder: "Inbox", subject: "", body_text: "", tags: "" });
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Create failed"));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="flex-1 min-h-0 overflow-y-auto p-6 space-y-4" data-testid="outreach-templates">
      <PageHeader
        title="Template library"
        description="Reusable outreach copy across email, WhatsApp, Telegram, and LinkedIn."
      />

      <div className="flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[180px]">
          <Label className="text-[11px]">Search</Label>
          <Input className="mt-1 h-9" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search templates…" />
        </div>
        <div>
          <Label className="text-[11px]">Folder</Label>
          <FilterSelect
            variant="field"
            className="mt-1 min-w-[140px]"
            value={folder}
            onChange={setFolder}
            options={[
              { value: "", label: "All folders" },
              ...folders.map((f) => ({ value: f, label: f })),
            ]}
            aria-label="Folder"
          />
        </div>
        <Button size="sm" variant="outline" onClick={load}>Refresh</Button>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3 max-w-2xl">
        <div className="text-[13px] font-semibold flex items-center gap-1.5"><Plus size={14} /> New template</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-[11px]">Name</Label>
            <Input className="mt-1 h-9" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <Label className="text-[11px]">Channel</Label>
            <FilterSelect
              variant="field"
              className="mt-1"
              value={form.channel}
              onChange={(channel) => setForm({ ...form, channel })}
              options={["email", "whatsapp", "telegram"].map((ch) => ({ value: ch, label: ch }))}
              aria-label="Channel"
            />
          </div>
          <div>
            <Label className="text-[11px]">Folder</Label>
            <Input className="mt-1 h-9" value={form.folder} onChange={(e) => setForm({ ...form, folder: e.target.value })} />
          </div>
          <div>
            <Label className="text-[11px]">Category</Label>
            <Input className="mt-1 h-9" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
          </div>
        </div>
        {form.channel === "email" && (
          <div>
            <Label className="text-[11px]">Subject</Label>
            <Input className="mt-1 h-9" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
          </div>
        )}
        <div>
          <Label className="text-[11px]">Body</Label>
          <Textarea className="mt-1 min-h-[96px]" value={form.body_text} onChange={(e) => setForm({ ...form, body_text: e.target.value })} placeholder="Hi {{first_name}}, …" />
        </div>
        <div>
          <Label className="text-[11px]">Tags (comma-separated)</Label>
          <Input className="mt-1 h-9" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} />
        </div>
        <Button size="sm" disabled={creating} onClick={handleCreate}>{creating ? "Saving…" : "Create template"}</Button>
      </div>

      <div className="space-y-2">
        {loading && <div className="text-[12.5px] text-slate-500">Loading…</div>}
        {!loading && templates.length === 0 && (
          <div className="text-[12.5px] text-slate-500 py-8 text-center border border-dashed border-slate-200 rounded-lg">
            No templates yet. Create one above.
          </div>
        )}
        {templates.map((t) => (
          <div key={t.id} className="rounded-lg border border-slate-200 bg-white p-4 flex gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[13px] font-semibold text-slate-900">{t.name}</span>
                <span className="text-[10.5px] uppercase tracking-wide text-slate-500">{t.channel}</span>
                <span className="text-[10.5px] text-slate-400">v{t.version}</span>
                {t.is_favorite && <Star size={12} className="text-amber-500" />}
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">{t.folder} · {t.category}</div>
              <div className="text-[12.5px] text-slate-700 mt-2 line-clamp-2">{t.preview_text || t.body_text}</div>
            </div>
            <div className="flex flex-col gap-1 shrink-0">
              <Button
                size="sm"
                variant="ghost"
                className="h-7"
                onClick={async () => {
                  try {
                    await outreachApi.updateTemplate(t.id, { is_favorite: !t.is_favorite });
                    await load();
                  } catch (e) {
                    toast.error(apiErrorMessage(e, "Update failed"));
                  }
                }}
              >
                <Star size={12} />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-7"
                onClick={async () => {
                  try {
                    await outreachApi.duplicateTemplate(t.id);
                    toast.success("Duplicated");
                    await load();
                  } catch (e) {
                    toast.error(apiErrorMessage(e, "Duplicate failed"));
                  }
                }}
              >
                <Copy size={12} />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 text-rose-700"
                onClick={async () => {
                  if (!window.confirm("Delete this template?")) return;
                  try {
                    await outreachApi.deleteTemplate(t.id);
                    toast.success("Deleted");
                    await load();
                  } catch (e) {
                    toast.error(apiErrorMessage(e, "Delete failed"));
                  }
                }}
              >
                <Trash2 size={12} />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
